import { DynamicSelectOption } from '@components/form/dynamic-fields';
import { SelectOption } from '@components/select';
import { getScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';

import type { ConditionComparator } from '../integrations-types';

export function getComparatorLabel(key: ConditionComparator) {
  const { t } = getScopedTranslate(labelLocale);
  const COMPARITOR_MAPPER: { [key in ConditionComparator]: string } = {
    eq: t('is'),
    neq: t('isNot'),
    contains: t('contains'),
    ncontains: t('notContain'),
    starts_with: t('startsWith'),
    ends_with: t('endsWith'),
    empty: t('empty'),
    nempty: t('notEmpty'),
    number_gt: t('moreThan'),
    number_lt: t('lessThan'),
    number_gte: t('moreOrEqualTo'),
    number_lte: t('lessOrEqualTo'),
    number_eq: t('equalTo'),
    number_neq: t('notEqualTo'),
    number_starts_with: t('startsWith'),
    number_ends_with: t('endsWith'),
    number_empty: t('empty'),
    number_nempty: t('notEmpty'),
    boolean_true: t('is'),
    boolean_false: t('is'),
    date_lt: t('before'),
    date_lte: t('isOrBefore'),
    date_gt: t('after'),
    date_gte: t('isOrAfter'),
    date_eq: t('is'),
    date_neq: t('isNot'),
    date_empty: t('empty'),
    date_nempty: t('notEmpty'),
    select_eq: t('is'),
    select_neq: t('isNot'),
    select_any_of: t('anyOf'),
    select_none_of: t('noneOf'),
    select_empty: t('empty'),
    select_nempty: t('notEmpty'),
    select_multi_eq: t('is'),
    select_multi_neq: t('isNot'),
    select_multi_any_of: t('anyOf'),
    select_multi_none_of: t('noneOf'),
    select_multi_empty: t('empty'),
    select_multi_nempty: t('notEmpty'),
    file_contains: t('contains'),
    file_ncontains: t('notContain'),
    file_starts_with: t('startsWith'),
    file_ends_with: t('endsWith'),
    file_empty: t('empty'),
    file_nempty: t('notEmpty'),
    changes_value: t('changesValue'),
  };
  return COMPARITOR_MAPPER[key as keyof typeof COMPARITOR_MAPPER];
}

type FormFile = {
  name: string;
  [key: string]: any;
};

export function getValueLabel(
  value: SelectOption | DynamicSelectOption[] | FormFile | string | undefined | number,
  comparator?: string
): string {
  const { t } = getScopedTranslate(labelLocale);

  if (comparator === 'boolean_true' && !value) {
    return t('booleanTrue');
  } else if (comparator === 'boolean_false' && !value) {
    return t('booleanFalse');
  } else if (
    value === undefined ||
    value === null ||
    (typeof value !== 'object' && String(value) === '')
  ) {
    return '';
  } else if (comparator?.startsWith('select_')) {
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((field: SelectOption) => field.label || '').join(', ');
    }

    return (value as SelectOption).label as string;
  } else if (comparator?.startsWith('file_')) {
    const filename = typeof value === 'string' ? value : (value as FormFile)?.name;
    return filename.split('/').pop() || '';
  } else if (comparator?.startsWith('date')) {
    return abbreviateDate(value as string, 'en');
  } else if (comparator?.startsWith('number')) {
    return formatNumber(value as number);
  }

  return ((value as SelectOption)?.label as string) ?? (value as string);
}

const labelLocale = {
  en: {
    is: 'is',
    isNot: 'is not',
    booleanTrue: 'true',
    booleanFalse: 'false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    contains: 'contains',
    notContain: 'does not contain',
    equalTo: 'equal to',
    notEqualTo: 'not equal to',
    empty: 'is empty',
    notEmpty: 'is not empty',
    anyOf: 'is any of',
    noneOf: 'is none of',
    moreThan: 'more than',
    moreOrEqualTo: 'more or equal to',
    lessThan: 'less than',
    lessOrEqualTo: 'less or equal to',
    startsWith: 'starts with',
    endsWith: 'ends with',
    changesValue: 'changes value',
  },
};
